import {
    CHROME_EXTENSION_URL,
    REVIEW_SOURCE_DEFINITIONS,
    type ReviewSourceId,
    type SourceKind,
} from '@flexbyt/shared';

/**
 * The source acquisition pages.
 *
 * ===========================================================================
 * Written per platform, not templated
 * ===========================================================================
 * The brief is explicit that these must not be one article with the source name
 * swapped, and the reason is not stylistic: a hotel manager and an Etsy seller
 * have almost nothing in common, and a page that addresses "your customers"
 * generically addresses neither. Every field below says something that is only
 * true of this platform and the people on it.
 *
 * ===========================================================================
 * How something imports is read, not written
 * ===========================================================================
 * `kind` comes from `REVIEW_SOURCE_DEFINITIONS` in the shared package — the same
 * value the dashboard branches on. So a page cannot claim a direct import for a
 * source the product collects through the extension, and the day one of them
 * changes mechanism the pages move with it.
 */

export type IntegrationContent = {
  id: ReviewSourceId;
  /** `/integrations/google-reviews`. */
  slug: string;
  /** How the brand writes its own name. */
  name: string;

  /** The h1. */
  headline: string;
  /** Two sentences under it. */
  intro: string;
  /** Who this page is for, in their own words. */
  audience: string;

  /** What somebody pastes, and where they find it. */
  urlLabel: string;
  urlPlaceholder: string;
  urlHint: string;
  /** Hostnames a valid listing lives on. Used as a hint, never as a gate. */
  hosts: string[];

  /** Three situations, specific to this platform's users. */
  useCases: { title: string; body: string }[];
  /** Whether several listings of this platform is a normal thing to have. */
  multiple?: string;
  faqs: { q: string; a: string }[];
};

/**
 * The sources ReviewsJet fetches server-side from a public page.
 *
 * You paste a link, ReviewsJet reads it, the reviews arrive. That is a
 * different promise from the extension sources below, and the two are kept in
 * separate lists so no component can flatten them into one grid with a badge.
 */
export const DIRECT_INTEGRATIONS: IntegrationContent[] = [
  {
    id: 'google',
    slug: 'google-reviews',
    name: 'Google',
    headline: 'Put your Google reviews on your own website.',
    intro:
      'Your Google Business Profile is doing its work on Google. The people who reach your own website have already left that page behind, and every review you have earned stayed there.',
    audience: 'Local businesses with a Google Business Profile',
    urlLabel: 'Your Google Business Profile or Maps link',
    urlPlaceholder: 'https://maps.google.com/?cid=12345678901234567890',
    urlHint:
      'Search your business on Google Maps, open its listing, and copy the address from the bar. A share link works too.',
    hosts: ['google.com', 'google.co.uk', 'maps.app.goo.gl', 'goo.gl', 'g.page'],
    multiple:
      'Several shops, branches or offices? Each Google listing connects separately — "Google — New York", "Google — London" — and they all land in one library. You choose whether a widget shows one location or all of them.',
    useCases: [
      {
        title: 'A trade or service business',
        body: 'The people who find you through a search will check your website before calling. Your Google rating should be on it.',
      },
      {
        title: 'A shop with a storefront',
        body: 'Reviews from the people who walked in, on the page that gets them to walk in.',
      },
      {
        title: 'Several locations',
        body: 'Connect each listing separately and show the right reviews on the right page.',
      },
    ],
    faqs: [
      {
        q: 'Where do I find my Google link?',
        a: 'Search for your business on Google Maps, open the listing, and copy the URL from your browser. The "Share" button gives you a shorter link that also works.',
      },
      {
        q: 'Do new Google reviews appear automatically?',
        a: 'Yes. ReviewsJet re-checks your listing and brings in new reviews as they arrive — you do not paste the link again.',
      },
      {
        q: 'Can I choose which reviews show?',
        a: 'Every review lands in your library unpublished until you say otherwise. Publish the ones you want, leave the rest, and change your mind whenever.',
      },
      {
        q: 'Does this affect my Google listing?',
        a: 'No. ReviewsJet reads your public reviews; it does not post, reply or change anything on Google.',
      },
    ],
  },
  {
    id: 'trustpilot',
    slug: 'trustpilot-reviews',
    name: 'Trustpilot',
    headline: 'Show your Trustpilot reviews where people decide.',
    intro:
      'A Trustpilot profile is a page somebody has to go and find, and most never do. The same reviews on your pricing page are in front of the person who is already weighing you up.',
    audience: 'Companies already collecting customer reviews on Trustpilot',
    urlLabel: 'Your Trustpilot review page',
    urlPlaceholder: 'https://www.trustpilot.com/review/yourdomain.com',
    urlHint:
      'It is trustpilot.com/review/ followed by your domain. Open your profile and copy the address.',
    hosts: ['trustpilot.com'],
    useCases: [
      {
        title: 'On a pricing page',
        body: 'The moment somebody is deciding is the moment other customers are most persuasive.',
      },
      {
        title: 'Alongside a signup form',
        body: 'A wall of reviews beside the form answers the question the form cannot.',
      },
      {
        title: 'For a company with a long history there',
        body: 'Years of accumulated reviews are an asset sitting on a domain you do not own.',
      },
    ],
    faqs: [
      {
        q: 'Which reviews are imported?',
        a: 'The public reviews on your Trustpilot profile. You then choose which of those appear on your site.',
      },
      {
        q: 'Do I need a paid Trustpilot plan?',
        a: 'No. ReviewsJet reads your public profile page, which is the same page anybody can visit.',
      },
      {
        q: 'Can I show only four and five star reviews?',
        a: 'You publish reviews one by one, so you decide exactly what appears. You can also filter a widget by tag.',
      },
    ],
  },
  {
    id: 'yelp',
    slug: 'yelp-reviews',
    name: 'Yelp',
    headline: 'Bring your Yelp reviews onto your own site.',
    intro:
      'Yelp sends people to Yelp. Your reviews are the strongest thing on your listing, and they belong on the site you actually control — where somebody books, orders or calls.',
    audience: 'Restaurants, bars and local service businesses',
    urlLabel: 'Your Yelp business page',
    urlPlaceholder: 'https://www.yelp.com/biz/your-business',
    urlHint:
      'Open your business page on Yelp and copy the address. It looks like yelp.com/biz/your-business-name.',
    hosts: ['yelp.com', 'yelp.co.uk', 'yelp.ca'],
    multiple:
      'More than one location on Yelp? Connect each business page separately — they arrive in the same library, still labelled with which one they came from.',
    useCases: [
      {
        title: 'A restaurant or bar',
        body: 'People read reviews before they book a table. Put them next to the booking button.',
      },
      {
        title: 'A service business',
        body: 'Plumbers, dentists, salons — the reviews that win the call should be on the page that takes it.',
      },
      {
        title: 'A second or third site',
        body: 'One listing per location, one library, and a widget per page if you want it.',
      },
    ],
    faqs: [
      {
        q: 'Where is my Yelp page URL?',
        a: 'Search your business on Yelp, open its page, and copy the address. It contains /biz/ followed by your business name.',
      },
      {
        q: 'Will this get me in trouble with Yelp?',
        a: 'ReviewsJet reads the public reviews on your listing. It does not post, vote, or alter anything on Yelp.',
      },
      {
        q: 'Can I show the reviewer’s photo?',
        a: 'Yes, where Yelp shows one — and you can turn it off if you would rather show names only.',
      },
    ],
  },
  {
    id: 'tripadvisor',
    slug: 'tripadvisor-reviews',
    name: 'Tripadvisor',
    headline: 'Your Tripadvisor reviews, on your own booking page.',
    intro:
      'Guests research on Tripadvisor and book direct when they can — it costs them less and costs you no commission. Showing the reviews that convinced them, at the moment they book, closes that gap.',
    audience: 'Hotels, restaurants, attractions and travel businesses',
    urlLabel: 'Your Tripadvisor listing',
    urlPlaceholder: 'https://www.tripadvisor.com/Hotel_Review-g123-d456-Reviews-Your_Hotel.html',
    urlHint:
      'Open your property, restaurant or attraction on Tripadvisor and copy the address of its review page.',
    hosts: ['tripadvisor.com', 'tripadvisor.co.uk', 'tripadvisor.ca', 'tripadvisor.in'],
    multiple:
      'A group with several properties, or a hotel with its own restaurant? Each listing connects on its own, so the restaurant page can show restaurant reviews and the rooms page can show stays.',
    useCases: [
      {
        title: 'Direct booking pages',
        body: 'Every direct booking is a commission you keep. Reviews beside the booking form are what make one happen.',
      },
      {
        title: 'A hotel restaurant',
        body: 'Its own listing, its own reviews, on its own page — rather than the hotel’s reviews standing in.',
      },
      {
        title: 'Attractions and tours',
        body: 'People buy tickets on the strength of what other visitors said. Put that on the ticket page.',
      },
    ],
    faqs: [
      {
        q: 'Which Tripadvisor link do I need?',
        a: 'The review page for your property, restaurant or attraction — the one with Reviews in the address. Copy it straight from your browser.',
      },
      {
        q: 'Will guest photos come across?',
        a: 'Where the review has them, yes, and the masonry and popup layouts open them at full size. You can hide photos entirely if you prefer.',
      },
      {
        q: 'Can I show reviews from more than one property?',
        a: 'Yes. Connect each listing and they share one library, so a group site can show everything and each property page can show its own.',
      },
    ],
  },
  {
    id: 'airbnb',
    slug: 'airbnb-reviews',
    name: 'Airbnb',
    headline: 'Show your Airbnb guest reviews on your direct booking site.',
    intro:
      'Years of five-star stays sit inside a listing you do not own, working for a platform that takes a cut. On your own site they are the reason somebody books with you instead.',
    audience: 'Hosts and property managers with guest reviews',
    urlLabel: 'Your Airbnb listing',
    urlPlaceholder: 'https://www.airbnb.com/rooms/12345678',
    urlHint:
      'Open your listing on Airbnb and copy the address. Anything after the room number is trimmed automatically.',
    hosts: ['airbnb.com', 'airbnb.co.uk', 'airbnb.ca', 'abnb.me'],
    multiple:
      'Managing several properties? Each listing connects separately — "Airbnb — Loft 4B", "Airbnb — The Cottage" — so a property page can show that property’s own guests.',
    useCases: [
      {
        title: 'A direct booking site',
        body: 'The whole argument for booking direct is that it is cheaper for the guest. Reviews are what make it feel safe.',
      },
      {
        title: 'Several properties',
        body: 'One listing per property, so each page shows the guests who actually stayed there.',
      },
      {
        title: 'A new listing elsewhere',
        body: 'Reviews earned on Airbnb still count when you list somewhere with none.',
      },
    ],
    faqs: [
      {
        q: 'Do I need the long link with all the parameters?',
        a: 'No. Paste whatever your browser gives you — the dates, guest counts and tracking parameters are trimmed, so the same listing is never connected twice.',
      },
      {
        q: 'Are guest names shown in full?',
        a: 'Airbnb shows first names, and that is what arrives. You can hide names entirely if you would rather.',
      },
      {
        q: 'Does this break Airbnb’s rules?',
        a: 'ReviewsJet reads the public reviews on your listing, which anybody can see. It does not contact guests or change anything on Airbnb.',
      },
    ],
  },
  {
    id: 'googleplay',
    slug: 'google-play-reviews',
    name: 'Google Play',
    headline: 'Put your Google Play reviews on your app’s website.',
    intro:
      'Android users rate your app in the store, and the people reading your landing page never see it. Bring those reviews to the page where somebody decides whether to download.',
    audience: 'App makers and SaaS teams with an Android app',
    urlLabel: 'Your app’s Google Play link',
    urlPlaceholder: 'https://play.google.com/store/apps/details?id=com.example.app',
    urlHint:
      'Open your app on play.google.com and copy the address. It contains ?id= followed by your package name.',
    hosts: ['play.google.com'],
    useCases: [
      {
        title: 'An app landing page',
        body: 'The store rating is the first thing a person checks. Put it on the page that sends them to the store.',
      },
      {
        title: 'A SaaS product with a mobile app',
        body: 'Reviews of the app prove the product works on the phone, not only in a browser tab.',
      },
      {
        title: 'Your developer replies',
        body: 'Where you answered a review on Google Play, the reply comes across with it.',
      },
    ],
    faqs: [
      {
        q: 'Which language’s reviews come in?',
        a: 'Google Play sorts reviews by language. The language and country in your link — the hl= and gl= parts — decide which ones are read, so copy the link from the store you care about.',
      },
      {
        q: 'Do new Play reviews appear automatically?',
        a: 'Yes. ReviewsJet re-checks your app’s page and brings in new reviews as people leave them.',
      },
      {
        q: 'Do I need access to the Play Console?',
        a: 'No. ReviewsJet reads the public reviews on your store page. Nothing is connected to your developer account and nothing is posted.',
      },
    ],
  },
  {
    id: 'appstore',
    slug: 'app-store-reviews',
    name: 'App Store',
    headline: 'Show your App Store reviews where people decide to download.',
    intro:
      'iPhone users leave their verdict in the App Store, one storefront at a time. Put the best of it on your own site, next to the download button.',
    audience: 'iOS developers and product teams with an iPhone or iPad app',
    urlLabel: 'Your app’s App Store link',
    urlPlaceholder: 'https://apps.apple.com/us/app/your-app/id123456789',
    urlHint:
      'Open your app on apps.apple.com and copy the address. It ends in /id followed by numbers.',
    hosts: ['apps.apple.com', 'itunes.apple.com'],
    multiple:
      'Reviews live per country on the App Store. Connect the US store and the UK store separately and they land in one library — or show each country on its own localised page.',
    useCases: [
      {
        title: 'A download page',
        body: 'Five stars and a sentence from a real user, beside the App Store badge, does more than the badge alone.',
      },
      {
        title: 'Localised sites',
        body: 'Each storefront connects on its own, so the French page can show what French users said.',
      },
      {
        title: 'Launch and press pages',
        body: 'New reviews of a fresh release arrive on their own, so the page keeps up with the version people are using.',
      },
    ],
    faqs: [
      {
        q: 'Which country’s reviews do I get?',
        a: 'The one in your link — apps.apple.com/us/… is the US store, /gb/ the UK and so on. A link with no country reads the US store.',
      },
      {
        q: 'Are reviewer photos included?',
        a: 'The App Store does not show reviewer photos, so App Store reviews appear with an initial instead. The title, stars, text and date all come across.',
      },
      {
        q: 'Does this need App Store Connect?',
        a: 'No. ReviewsJet reads the reviews anyone can see on your app’s page. It never signs in to your developer account.',
      },
    ],
  },
];

/**
 * The marketplaces the Chrome extension collects from.
 *
 * A separate list, and separate pages, because the mechanism is genuinely
 * different and the brief forbids presenting them as the same. These sites
 * cannot be read from a server: the extension picks the reviews up in the
 * seller's own browser, on their own page, while they are signed in.
 *
 * A visitor who expects "paste a link" and gets "install an extension" has been
 * misled, and they find out after signing up.
 */
export const EXTENSION_INTEGRATIONS: IntegrationContent[] = [
  {
    id: 'etsy',
    slug: 'etsy-reviews',
    name: 'Etsy',
    headline: 'Bring your Etsy reviews to your own shop.',
    intro:
      'Etsy reviews stay on Etsy, where they sell for Etsy. On the shop you own they are the difference between a first-time visitor and a first order.',
    audience: 'Etsy sellers who also run a shop of their own',
    urlLabel: 'Your Etsy shop reviews page',
    urlPlaceholder: 'https://www.etsy.com/shop/YourShop/reviews',
    urlHint: 'Open your shop’s reviews page while signed in to Etsy.',
    hosts: ['etsy.com'],
    useCases: [
      {
        title: 'Launching your own storefront',
        body: 'A new shop with no reviews looks new. Years of Etsy feedback fixes that on day one.',
      },
      {
        title: 'Product pages',
        body: 'Reviews of the thing somebody is looking at, rather than of the shop in general.',
      },
      {
        title: 'Selling in more than one place',
        body: 'The proof follows you rather than staying on the marketplace.',
      },
    ],
    faqs: [
      {
        q: 'Why do I need the extension for Etsy?',
        a: 'Etsy shop reviews cannot be read from a server the way a Google listing can. The ReviewsJet extension collects them in your own browser while you are signed in and looking at your own reviews page, then sends them to your library.',
      },
      {
        q: 'Is the extension free?',
        a: 'Yes, and you only need it for the marketplaces that work this way — Etsy, Amazon, AliExpress and TourRadar.',
      },
      {
        q: 'Do new Etsy reviews come in automatically?',
        a: 'No. Because collection happens in your browser, you open the page and run the extension when you want to bring new ones in. The five direct sources refresh on their own.',
      },
    ],
  },
  {
    id: 'amazon',
    slug: 'amazon-reviews',
    name: 'Amazon',
    headline: 'Bring your Amazon product reviews to your own store.',
    intro:
      'The reviews that sell the product on Amazon are the ones missing from the product page you own — where you keep the margin and the customer.',
    audience: 'Brands selling on Amazon as well as their own store',
    urlLabel: 'Your Amazon product reviews page',
    urlPlaceholder: 'https://www.amazon.com/product-reviews/B0XXXXXXX',
    urlHint: 'Open the reviews page for your product while signed in to Amazon.',
    hosts: ['amazon.com', 'amazon.co.uk', 'amazon.de', 'amazon.ca'],
    useCases: [
      {
        title: 'Your own product pages',
        body: 'Same product, same reviews, without sending the customer to a marketplace.',
      },
      {
        title: 'A new direct-to-consumer site',
        body: 'Launch with the reviews you already earned rather than none.',
      },
      {
        title: 'Wholesale and trade pages',
        body: 'Buyers checking a product want to see what end customers said.',
      },
    ],
    faqs: [
      {
        q: 'Why is Amazon different from Google?',
        a: 'Amazon review pages cannot be fetched from a server. The ReviewsJet Chrome extension reads them in your own browser, on your own product page, and sends them to your library.',
      },
      {
        q: 'Does it import reviews for products I do not sell?',
        a: 'It collects from whichever product reviews page you have open, so it is entirely up to you which ones.',
      },
      {
        q: 'How often should I run it?',
        a: 'Whenever you want new reviews brought across. There is no automatic refresh for extension sources.',
      },
    ],
  },
  {
    id: 'aliexpress',
    slug: 'aliexpress-reviews',
    name: 'AliExpress',
    headline: 'Import AliExpress product reviews with the extension.',
    intro:
      'Product reviews with photographs are the proof a new store has none of. If your products already have them on AliExpress, they can be on your own site instead.',
    audience: 'Sellers sourcing or selling through AliExpress',
    urlLabel: 'Your AliExpress product page',
    urlPlaceholder: 'https://www.aliexpress.com/item/1005000000000.html',
    urlHint: 'Open the product page with its reviews while signed in.',
    hosts: ['aliexpress.com', 'aliexpress.us'],
    useCases: [
      {
        title: 'A new store with no reviews',
        body: 'Photographs from real buyers do more than any amount of product copy.',
      },
      {
        title: 'Product pages',
        body: 'Reviews of the specific item somebody is looking at.',
      },
      {
        title: 'Testing a product before committing',
        body: 'Show what buyers already say rather than waiting to collect your own.',
      },
    ],
    faqs: [
      {
        q: 'Why does this need the extension?',
        a: 'AliExpress product reviews cannot be read from a server. The extension collects them in your browser while you are on the product page.',
      },
      {
        q: 'Do photographs come across?',
        a: 'Where the review has them. The masonry and popup layouts open them at full size.',
      },
      {
        q: 'Do new reviews arrive on their own?',
        a: 'No. Because collection happens in your browser, you run the extension again when you want to bring new ones in. The five direct sources refresh on their own.',
      },
      {
        q: 'Can I edit or remove a review afterwards?',
        a: 'You can delete any review from your library, and nothing appears on your site until you publish it.',
      },
    ],
  },
  {
    id: 'tourradar',
    slug: 'tourradar-reviews',
    name: 'TourRadar',
    headline: 'Show your TourRadar reviews on your own tours.',
    intro:
      'Travellers compare operators on reviews, and yours are on a marketplace that takes a commission. On your own booking page they work for you.',
    audience: 'Tour operators listed on TourRadar',
    urlLabel: 'Your TourRadar operator page',
    urlPlaceholder: 'https://www.tourradar.com/o/your-operator',
    urlHint: 'Open your operator page with its reviews while signed in.',
    hosts: ['tourradar.com'],
    useCases: [
      {
        title: 'Direct booking pages',
        body: 'A commission saved on every booking that comes to you instead.',
      },
      {
        title: 'Individual tour pages',
        body: 'What previous travellers said about the trip somebody is looking at.',
      },
      {
        title: 'A new operator site',
        body: 'Launch with the reviews you already have rather than an empty page.',
      },
    ],
    faqs: [
      {
        q: 'Why the extension rather than a link?',
        a: 'TourRadar pages cannot be fetched from a server. The ReviewsJet extension reads them in your own browser while you are signed in.',
      },
      {
        q: 'Is there an automatic refresh?',
        a: 'No — extension sources are collected when you run them. The five direct sources refresh on their own.',
      },
      {
        q: 'Can I show reviews for one tour only?',
        a: 'Tag them as they come in and point a widget at that tag.',
      },
    ],
  },
];

export const ALL_INTEGRATIONS = [
  ...DIRECT_INTEGRATIONS,
  ...EXTENSION_INTEGRATIONS,
];

export function integrationBySlug(slug: string): IntegrationContent | undefined {
  return ALL_INTEGRATIONS.find((entry) => entry.slug === slug);
}

export function integrationPath(entry: IntegrationContent): string {
  return `/integrations/${entry.slug}`;
}

/**
 * The page for a source, by id.
 *
 * The widget pages, the free tools and the 404 all name sources in passing and
 * need somewhere to send people. One helper rather than a path composed at each
 * call site, so moving these pages is one edit.
 */
export function sourcePagePath(id: ReviewSourceId): string {
  const entry = ALL_INTEGRATIONS.find((candidate) => candidate.id === id);
  // Every source the product can import from has a page; the index is the
  // honest fallback if one is ever added here before its page is written.
  return entry ? integrationPath(entry) : '/integrations';
}

/** How this source imports, straight from the product's own definition. */
export function integrationKind(id: ReviewSourceId): SourceKind {
  return REVIEW_SOURCE_DEFINITIONS[id].kind;
}

export { CHROME_EXTENSION_URL };
